import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import Container from "components/Container";
import Dialog from "components/Dialog";
import FullScreenSpinner from "components/FullScreenSpinner";
import Input from "components/Input";
import LoginForm from "components/LoginForm";
import Spacer from "components/Spacer";
import TenantLogo from "components/TenantLogo";
import useAuth from "hooks/useAuth";
import { useLogout } from "hooks/useLogout";
import {
  ComparisonRowV3,
  ShopCategoryV3,
  ShopProductV3,
} from "lib/sanity.types";
import { ShopV3ScreenProps } from "navigation/types";
import { useFetchShopCatalogV3Query } from "queries/cms.queries";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { useFetchPlanPackagesQuery } from "queries/planPackage.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import { createContactFetcher } from "queries/user.queries";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, View } from "react-native";
import tinycolor from "tinycolor2";
import { toDashboard } from "utils/navigationUtils";
import CheckoutV3, { PurchaseSummary } from "./CheckoutV3";
import ComparisonMatrixV3, {
  ComparisonColumn,
} from "./components/ComparisonMatrixV3";
import HeroBannerV3 from "./components/HeroBannerV3";
import ProductCardV3 from "./components/ProductCardV3";
import SessionBarV3 from "./components/SessionBarV3";
import {
  buildLiveCatalog,
  categoryPriceLabel,
  defaultVariant,
  isEmailCaptureEnabled,
  isValidEmail,
  LINE,
  LiveCategory,
  LiveProduct,
  LiveVariant,
  PANEL,
  PAPER,
  resolveUpgradeOffer,
  unitPriceLabel,
} from "./helpers";
import SeasonCheckoutV3 from "./SeasonCheckoutV3";

interface ComparisonState {
  title: string;
  columns: ComparisonColumn[];
  rows: ComparisonRowV3[];
  perVariantValues: Record<string, string>;
}

interface PendingPurchase {
  product: ShopProductV3;
  live: LiveVariant;
  quantity: number;
}

type ShopView = "landing" | "detail" | "checkout" | "confirmation";

// The plan name a shopper would recognize: the variant label for programs
// (e.g. "Kids Club Plus"), the product name otherwise (e.g. "Family Pass").
const planDisplayName = (purchase: PendingPurchase): string =>
  purchase.product.kind === "program"
    ? purchase.live.variant.label
    : purchase.product.name;

const ShopV3 = ({ navigation, route }: ShopV3ScreenProps) => {
  const auth = useAuth();
  const { logoutAccount } = useLogout();

  const { data: branding, isFetching: fetchingBranding } =
    useFetchTenantBrandingQuery();
  // Populates the query cache formatCurrency reads from.
  useFetchTenantConfigurationQuery();
  // The tenant subdomain is the first host label (same resolution the API
  // client uses), which lets the catalog fetch run in parallel with
  // branding instead of waterfalling behind it; branding remains the
  // fallback for hosts without a tenant label.
  const hostSubdomain =
    Platform.OS === "web" && window.location.hostname.split(".").length >= 3
      ? window.location.hostname.split(".")[0]
      : undefined;
  const { data: catalog, isFetching: fetchingCatalog } =
    useFetchShopCatalogV3Query({
      subdomain: hostSubdomain ?? branding?.subdomain,
    });
  const { data: planPackages, isFetching: fetchingPlanPackages } =
    useFetchPlanPackagesQuery();
  const { mutateAsync: createContact } = useMutation(createContactFetcher);

  const [view, setView] = useState<ShopView>("landing");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [variantSelections, setVariantSelections] = useState<
    Record<string, string>
  >({});
  // Which product is shown in each category's detail view (tab bar).
  const [productSelections, setProductSelections] = useState<
    Record<string, string>
  >({});
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [checkout, setCheckout] = useState<PendingPurchase | null>(null);
  // Checkout upgrade offer state; per V7's contract there is at most one
  // eligible offer, and a dismissal sticks for that checkout.
  const [upgradeApplied, setUpgradeApplied] = useState(false);
  const [upgradeDismissed, setUpgradeDismissed] = useState(false);
  const [summary, setSummary] = useState<PurchaseSummary | null>(null);
  const [comparison, setComparison] = useState<ComparisonState | null>(null);
  const [loginVisible, setLoginVisible] = useState(false);

  // Email gate state; capturedEmailRef also prefills the checkout form.
  const [pendingPurchase, setPendingPurchase] =
    useState<PendingPurchase | null>(null);
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const capturedEmailRef = useRef<string | null>(null);
  const scrollRef = useRef<ScrollView>(null);
  const deepLinkApplied = useRef(false);

  const liveCatalog: LiveCategory[] = useMemo(() => {
    if (!catalog || !planPackages) return [];
    return buildLiveCatalog(catalog, planPackages);
  }, [catalog, planPackages]);

  const selectedCategory =
    liveCatalog.find((entry) => entry.category._id === selectedCategoryId) ??
    liveCatalog[0] ??
    null;

  const priceStrategy = catalog?.priceDisplayStrategy ?? "lowest-monthly";
  const primaryColor = branding?.primaryColor || "#111827";
  const selectedTabColor = tinycolor(primaryColor).darken(10).toHexString();
  const appbarBackground = branding?.secondaryColor || "#211c1d";
  // Light text on a dark bar; flips for tenants with a light secondary color.
  const appbarTextIsLight = !tinycolor(appbarBackground).isLight();

  useEffect(() => {
    if (catalog?.heading) navigation.setOptions({ title: catalog.heading });
  }, [catalog?.heading, navigation]);

  // Tenant guard: this shop only exists for the tenant the host resolves to.
  useEffect(() => {
    if (fetchingBranding || fetchingCatalog) return;
    if (!branding?.subdomain) return;
    if (catalog === null && Platform.OS === "web") {
      window.location.href = "/login";
    }
  }, [branding?.subdomain, catalog, fetchingBranding, fetchingCatalog]);

  // LoginForm has no success callback — it only flips the redux auth state,
  // so close the sign-in dialog ourselves once the user is logged in.
  useEffect(() => {
    if (auth.isLoggedIn) setLoginVisible(false);
  }, [auth.isLoggedIn]);

  // Deep link: /shop_v3/:slug/:category/:product opens that category's
  // detail view with the product tab preselected. Applied once, after the
  // live catalog is ready.
  useEffect(() => {
    if (deepLinkApplied.current || liveCatalog.length === 0) return;
    deepLinkApplied.current = true;
    const target = route.params.category
      ? liveCatalog.find(
          (entry) => entry.category.slug.current === route.params.category,
        )
      : null;
    if (!target) return;
    if (route.params.product) {
      const productTarget = target.products.find(
        (product) => product.product.slug.current === route.params.product,
      );
      if (productTarget) {
        setProductSelections((prev) => ({
          ...prev,
          [target.category._id]: productTarget.product._id,
        }));
      }
    }
    setSelectedCategoryId(target.category._id);
    setView("detail");
  }, [liveCatalog, route.params.category, route.params.product]);

  // Keep the URL shareable: the category segment mirrors the detail view.
  // Written via history.replaceState — react-navigation's setParams keeps a
  // cleared param in state and serializes it as a literal "undefined"
  // segment, and replaceState also avoids polluting the back stack.
  useEffect(() => {
    if (Platform.OS !== "web" || !deepLinkApplied.current) return;
    const base = `/shop_v3/${route.params.slug}`;
    const path =
      view === "detail" && selectedCategory
        ? `${base}/${selectedCategory.category.slug.current}/${
            selectedProductOf(selectedCategory).product.slug.current
          }`
        : base;
    if (window.location.pathname !== path) {
      // Preserve the query string and hash (UTM params, campaign tags).
      const { search, hash } = window.location;
      window.history.replaceState(null, "", `${path}${search}${hash}`);
    }
  }, [view, selectedCategory, productSelections, route.params.slug]);

  const emailError = useMemo(() => {
    if (!emailTouched) return null;
    if (email.trim().length === 0) return "Email is required.";
    if (!isValidEmail(email)) return "Enter a valid email address.";
    return null;
  }, [email, emailTouched]);

  const selectedLiveVariant = (product: LiveProduct): LiveVariant => {
    const wantedKey = variantSelections[product.product._id];
    return (
      product.variants.find(({ variant }) => variant.key === wantedKey) ??
      defaultVariant(product.variants)
    );
  };

  // Which of a category's products the detail view shows (tab bar choice).
  const selectedProductOf = (entry: LiveCategory): LiveProduct =>
    entry.products.find(
      (product) =>
        product.product._id === productSelections[entry.category._id],
    ) ?? entry.products[0];

  const liveProductOf = (productId: string): LiveProduct | null => {
    for (const entry of liveCatalog) {
      for (const product of entry.products) {
        if (product.product._id === productId) return product;
      }
    }
    return null;
  };

  const goTo = (nextView: ShopView) => {
    setView(nextView);
    scrollRef.current?.scrollTo({ y: 0, animated: false });
  };

  const startCheckout = (purchase: PendingPurchase) => {
    setCheckout(purchase);
    setUpgradeApplied(false);
    setUpgradeDismissed(false);
    goTo("checkout");
  };

  // The original selection stays in `checkout` (for undo); the accepted
  // upgrade swaps what the checkout actually purchases. The offer is
  // suppressed when the chosen quantity exceeds the destination's
  // per-order cap — eligibility, not silent clamping.
  const upgradeOffer = useMemo(() => {
    if (!checkout) return null;
    const resolved = resolveUpgradeOffer(liveCatalog, checkout.live.variant);
    if (!resolved) return null;
    const destinationMax = resolved.product.product.maxPerOrder ?? 1;
    return checkout.quantity <= destinationMax ? resolved : null;
  }, [liveCatalog, checkout]);
  const activeCheckout =
    checkout && upgradeOffer && upgradeApplied
      ? {
          product: upgradeOffer.product.product,
          live: upgradeOffer.live,
          quantity: checkout.quantity,
        }
      : checkout;

  // Fire-and-forget lead capture: it can never change the checkout
  // outcome, so it must never delay (or block) the transition either.
  const captureContact = (contactEmail: string, planPackageId: number) => {
    capturedEmailRef.current = contactEmail;
    createContact({
      email: contactEmail.toLowerCase(),
      source: "OTHER",
      planPackageId,
      buyflowStep: "Email Capture V3",
    }).catch(() => {
      // Lead capture must never block checkout.
    });
  };

  const handlePurchase = (purchase: PendingPurchase) => {
    if (!catalog) return;
    const gateEnabled = isEmailCaptureEnabled(
      catalog,
      purchase.product,
      purchase.live.variant,
    );
    if (!gateEnabled || capturedEmailRef.current) {
      startCheckout(purchase);
      return;
    }
    if (auth.isLoggedIn && auth.username) {
      // Known session: capture silently and continue.
      captureContact(auth.username, purchase.live.planPackage.id);
      startCheckout(purchase);
      return;
    }
    setPendingPurchase(purchase);
  };

  const handleEmailCaptureConfirm = () => {
    setEmailTouched(true);
    if (!pendingPurchase || !isValidEmail(email)) return;
    captureContact(email.trim(), pendingPurchase.live.planPackage.id);
    const purchase = pendingPurchase;
    setPendingPurchase(null);
    startCheckout(purchase);
  };

  const openCategoryComparison = (entry: LiveCategory) => {
    const rows = entry.category.comparison ?? [];
    if (rows.length === 0) return;
    const perVariantValues: Record<string, string> = {};
    entry.products.forEach((product) => {
      perVariantValues[product.product.slug.current] =
        selectedLiveVariant(product).variant.label;
    });
    setComparison({
      title: entry.category.comparisonLabel || "Compare benefits",
      columns: entry.products.map((product) => ({
        key: product.product.slug.current,
        label: product.product.name,
      })),
      rows,
      perVariantValues,
    });
  };

  const openProductComparison = (product: LiveProduct) => {
    const rows = product.product.comparison ?? [];
    if (rows.length === 0) return;
    setComparison({
      title: `Compare ${product.product.name}`,
      columns: product.variants.map(({ variant }) => ({
        key: variant.key,
        label: variant.label,
      })),
      rows,
      perVariantValues: {},
    });
  };

  const sessionPathLabel = (): string => {
    if (view === "checkout" && activeCheckout) {
      const quantitySuffix =
        activeCheckout.quantity > 1 ? ` ×${activeCheckout.quantity}` : "";
      return `${activeCheckout.product.name} · ${activeCheckout.live.variant.label}${quantitySuffix}`;
    }
    if (view === "confirmation" && summary) return summary.productName;
    return selectedCategory?.category.name ?? "Memberships";
  };

  if (fetchingBranding || fetchingCatalog || fetchingPlanPackages) {
    return <FullScreenSpinner text="Loading shop" />;
  }

  if (!catalog) return null;

  const renderCategoryTabs = () => (
    /* Segmented control (prototype's fgm-ladder-tabs): pill container,
       selected tab lifts on a white chip with a soft shadow. */
    <View
      className="flex-row"
      style={{
        backgroundColor: "#e8e0da",
        borderRadius: 16,
        padding: 5,
        gap: 4,
      }}
    >
      {liveCatalog.map((entry) => {
        const isSelected =
          entry.category._id === selectedCategory?.category._id;
        return (
          <Pressable
            key={entry.category._id}
            testID={`v3-tab-${entry.category.slug.current}`}
            accessibilityRole="tab"
            accessibilityState={{ selected: isSelected }}
            onPress={() => setSelectedCategoryId(entry.category._id)}
            className="flex-1 items-center justify-center"
            style={{
              minHeight: 50,
              borderRadius: 12,
              backgroundColor: isSelected ? PAPER : "transparent",
              ...(isSelected
                ? {
                    shadowColor: "#372226",
                    shadowOffset: { width: 0, height: 6 },
                    shadowOpacity: 0.14,
                    shadowRadius: 16,
                    elevation: 4,
                  }
                : {}),
            }}
          >
            <Text
              category="s1"
              style={{
                fontWeight: "600",
                color: isSelected ? selectedTabColor : "#251f20",
                textAlign: "center",
              }}
            >
              {entry.category.tabLabel}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );

  const renderLadderCard = (entry: LiveCategory) => (
    /* Elevated ladder card (prototype's fgm-ladder-card). */
    <View
      className="border p-5 my-6"
      style={{
        borderRadius: 18,
        borderColor: LINE,
        backgroundColor: PAPER,
        shadowColor: "#372226",
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.08,
        shadowRadius: 24,
        elevation: 5,
      }}
    >
      {entry.category.cardKicker ? (
        <Text
          category="c1"
          appearance="hint"
          style={{ fontWeight: "600", letterSpacing: 1.2 }}
        >
          {entry.category.cardKicker}
        </Text>
      ) : null}
      <View className="flex-row justify-between items-start mt-1">
        <Text
          className="flex-1 pr-3"
          style={{
            fontSize: 24,
            lineHeight: 29,
            fontWeight: "600",
            letterSpacing: -0.4,
          }}
        >
          {entry.category.name}
        </Text>
        <View className="items-end">
          <Text category="h6" style={{ color: primaryColor }}>
            {entry.products.every(
              (product) => product.product.kind === "season",
            )
              ? "By seat"
              : categoryPriceLabel(entry.products, priceStrategy)}
          </Text>
          {entry.category.priceNote ? (
            <Text category="c1" appearance="hint">
              {entry.category.priceNote}
            </Text>
          ) : null}
        </View>
      </View>
      {entry.category.detail ? (
        <Text category="p2" appearance="hint" style={{ marginTop: 8 }}>
          {entry.category.detail}
        </Text>
      ) : null}
      {entry.category.benefits?.length ? (
        <View className="mt-4" style={{ gap: 10 }}>
          {entry.category.benefits.map((benefit) => (
            <View key={benefit} className="flex-row" style={{ gap: 8 }}>
              <Text category="s1" style={{ color: primaryColor }}>
                ✓
              </Text>
              <Text category="p2" className="flex-1">
                {benefit}
              </Text>
            </View>
          ))}
        </View>
      ) : null}
      <Spacer margin={8} />
      <Button testID="v3-category-cta" onPress={() => goTo("detail")}>
        {entry.category.ctaText || "Explore"}
      </Button>
    </View>
  );

  const renderLanding = () => (
    <Container>
      <HeroBannerV3
        kicker={catalog.kicker}
        heading={catalog.heading}
        subheading={catalog.subheading}
        primaryColor={primaryColor}
      />
      {renderCategoryTabs()}
      {selectedCategory ? (
        renderLadderCard(selectedCategory)
      ) : (
        <View className="py-10 items-center">
          <Text category="p1" appearance="hint">
            No memberships are available right now.
          </Text>
        </View>
      )}
    </Container>
  );

  const renderDetail = (entry: LiveCategory) => {
    const category: ShopCategoryV3 = entry.category;
    const selectedProduct = selectedProductOf(entry);
    return (
      <Container>
        <Pressable
          testID="v3-back"
          onPress={() => goTo("landing")}
          className="py-4"
        >
          <Text category="s2" style={{ color: primaryColor }}>
            ← All memberships
          </Text>
        </Pressable>
        <Text category="c1" appearance="hint">
          {category.tabLabel.toUpperCase()}
        </Text>
        <Text category="h5">{category.selectionTitle || category.name}</Text>
        {category.selectionDescription ? (
          <Text category="p2" appearance="hint" style={{ marginBottom: 12 }}>
            {category.selectionDescription}
          </Text>
        ) : (
          <Spacer margin={6} />
        )}

        {category.comparisonLabel && (category.comparison?.length ?? 0) > 0 ? (
          <Pressable
            testID="v3-category-comparison-link"
            onPress={() => openCategoryComparison(entry)}
            className="mb-4"
          >
            <Text category="s2" style={{ color: primaryColor }}>
              {category.comparisonLabel}
            </Text>
          </Pressable>
        ) : null}

        {entry.products.length > 1 ? (
          /* Product tabs (prototype's fgm-tabs): one cell per product with
             its name and current price; the selected tab fills with the
             tenant color and only that product's card renders below. */
          <View className="flex-row mb-4" style={{ gap: 6 }}>
            {entry.products.map((product) => {
              const isSelected =
                product.product._id === selectedProduct.product._id;
              return (
                <Pressable
                  key={product.product._id}
                  testID={`v3-product-tab-${product.product.slug.current}`}
                  accessibilityRole="tab"
                  accessibilityState={{ selected: isSelected }}
                  onPress={() =>
                    setProductSelections((prev) => ({
                      ...prev,
                      [category._id]: product.product._id,
                    }))
                  }
                  className="flex-1 items-center justify-center px-1"
                  style={{
                    minHeight: 58,
                    borderRadius: 10,
                    borderWidth: 1,
                    borderColor: isSelected ? primaryColor : LINE,
                    backgroundColor: isSelected ? primaryColor : PANEL,
                  }}
                >
                  <Text
                    category="s2"
                    numberOfLines={1}
                    style={{
                      textAlign: "center",
                      color: isSelected ? "#ffffff" : "#6d6264",
                    }}
                  >
                    {product.product.name}
                  </Text>
                  <Text
                    category="c1"
                    style={{
                      color: isSelected ? "rgba(255,255,255,0.82)" : "#6d6264",
                    }}
                  >
                    {product.product.kind === "season"
                      ? "By seat"
                      : unitPriceLabel(
                          selectedLiveVariant(product).planPackage,
                          priceStrategy,
                        )}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ) : null}

        <ProductCardV3
          key={selectedProduct.product._id}
          product={selectedProduct}
          priceStrategy={priceStrategy}
          primaryColor={primaryColor}
          selectedVariantKey={variantSelections[selectedProduct.product._id]}
          quantity={quantities[selectedProduct.product._id] ?? 1}
          onSelectVariant={(variantKey) =>
            setVariantSelections((prev) => ({
              ...prev,
              [selectedProduct.product._id]: variantKey,
            }))
          }
          onQuantityChange={(quantity) =>
            setQuantities((prev) => ({
              ...prev,
              [selectedProduct.product._id]: quantity,
            }))
          }
          onPurchase={(selected, quantity) =>
            handlePurchase({
              product: selectedProduct.product,
              live: selected,
              quantity,
            })
          }
          onOpenComparison={openProductComparison}
          onGift={(planPackageId) =>
            navigation.navigate("BuyGift", { planPackageId })
          }
        />
        <Spacer margin={12} />
      </Container>
    );
  };

  const renderConfirmation = (purchaseSummary: PurchaseSummary) => (
    <Container>
      <View
        testID="v3-confirmation"
        className="border rounded-2xl p-6 my-8 items-center"
        style={{ borderColor: LINE, backgroundColor: PAPER }}
      >
        <Text category="h4" style={{ textAlign: "center" }}>
          {purchaseSummary.kidsClub
            ? "Welcome to the club!"
            : "You're all set!"}
        </Text>
        <Text
          category="p1"
          appearance="hint"
          style={{ textAlign: "center", marginTop: 6 }}
        >
          {purchaseSummary.productName} · {purchaseSummary.variantLabel} ·{" "}
          {purchaseSummary.amountLabel}
        </Text>
        {purchaseSummary.memberNames.length ? (
          <View className="mt-4 self-stretch">
            <Text category="s1">
              {purchaseSummary.memberNamesTitle ??
                "Members on this registration"}
            </Text>
            {purchaseSummary.memberNames.map((name) => (
              <Text key={name} category="p2">
                • {name}
              </Text>
            ))}
          </View>
        ) : null}
        <View className="mt-4 self-stretch">
          <Text category="s1">What happens next</Text>
          <Text category="p2">
            • A confirmation email is on its way to you.
          </Text>
          <Text category="p2">
            {purchaseSummary.kidsClub
              ? "• Your guardian account holds the plan for every child listed above."
              : "• Your membership is active on your account."}
          </Text>
        </View>
        <Spacer margin={8} />
        <Button testID="v3-go-dashboard" onPress={() => toDashboard()}>
          Go to my dashboard
        </Button>
      </View>
    </Container>
  );

  const renderBody = () => {
    if (view === "checkout" && checkout && activeCheckout) {
      // Season tickets are seat-priced and hand off to the dedicated seat
      // flow (all live variants become its plan selector).
      if (checkout.product.kind === "season") {
        const seasonProduct = liveProductOf(checkout.product._id);
        if (seasonProduct) {
          return (
            <SeasonCheckoutV3
              product={checkout.product}
              variants={seasonProduct.variants}
              initialVariantKey={checkout.live.variant.key}
              initialEmail={capturedEmailRef.current ?? ""}
              primaryColor={primaryColor}
              onBack={() => setView("detail")}
              onComplete={(purchaseSummary) => {
                setSummary(purchaseSummary);
                goTo("confirmation");
              }}
            />
          );
        }
      }
      return (
        <CheckoutV3
          product={activeCheckout.product}
          live={activeCheckout.live}
          quantity={activeCheckout.quantity}
          initialEmail={capturedEmailRef.current ?? ""}
          primaryColor={primaryColor}
          onRequestLogin={() => setLoginVisible(true)}
          onBack={() => setView("detail")}
          onComplete={(purchaseSummary) => {
            setSummary(purchaseSummary);
            goTo("confirmation");
          }}
          upgrade={
            upgradeOffer && !upgradeDismissed
              ? {
                  applied: upgradeApplied,
                  destinationProduct: upgradeOffer.product.product,
                  destinationLive: upgradeOffer.live,
                  sourcePlanPackage: checkout.live.planPackage,
                  headline: upgradeOffer.headline,
                  note: upgradeOffer.note,
                  benefits: upgradeOffer.benefits,
                  onAccept: () => setUpgradeApplied(true),
                  onUndo: () => setUpgradeApplied(false),
                  onDismiss: () => {
                    setUpgradeApplied(false);
                    setUpgradeDismissed(true);
                  },
                }
              : undefined
          }
        />
      );
    }
    if (view === "confirmation" && summary) return renderConfirmation(summary);
    if (view === "landing" || !selectedCategory) return renderLanding();
    return renderDetail(selectedCategory);
  };

  return (
    <View className="flex-1" style={{ backgroundColor: PANEL }}>
      {/* Custom appbar (prototype's fgm-appbar): dark tenant-color bar with
          logo and brand line — deliberately no login button; the session
          bar at the bottom owns sign-in/out. */}
      <View
        testID="v3-appbar"
        className="flex-row items-center px-4"
        style={{ minHeight: 64, gap: 10, backgroundColor: appbarBackground }}
      >
        <TenantLogo
          isDarkBackground={appbarTextIsLight}
          height={40}
          width={48}
        />
        <Text
          category="s1"
          numberOfLines={1}
          style={{ color: appbarTextIsLight ? "#ffffff" : "#211c1d" }}
        >
          {branding?.name ? `${branding.name} Memberships` : "Memberships"}
        </Text>
      </View>

      <ScrollView ref={scrollRef} className="flex-1">
        {renderBody()}
      </ScrollView>

      <SessionBarV3
        pathLabel={sessionPathLabel()}
        isLoggedIn={auth.isLoggedIn}
        username={auth.username}
        primaryColor={primaryColor}
        onSignIn={() => setLoginVisible(true)}
        onSignOut={() => logoutAccount()}
      />

      <Dialog
        visible={loginVisible}
        onCancel={() => setLoginVisible(false)}
        cancelText="Close"
      >
        <LoginForm title="Sign in to continue" allowSignup={false} />
      </Dialog>

      <Dialog
        visible={comparison !== null}
        onCancel={() => setComparison(null)}
        cancelText="Close"
        style={{ maxWidth: 640 }}
      >
        {comparison ? (
          <View>
            <Text category="h6" style={{ marginBottom: 10 }}>
              {comparison.title}
            </Text>
            <ComparisonMatrixV3
              columns={comparison.columns}
              rows={comparison.rows}
              perVariantValues={comparison.perVariantValues}
            />
          </View>
        ) : null}
      </Dialog>

      <Dialog
        visible={pendingPurchase !== null}
        onCancel={() => {
          setPendingPurchase(null);
          setEmailTouched(false);
        }}
        cancelText="Back"
        okText="Continue"
        onOk={handleEmailCaptureConfirm}
        okBtnProps={{ testID: "v3-email-gate-continue" }}
      >
        <View testID="v3-email-gate">
          <Text category="h6">Let’s get started</Text>
          <Text category="p2" appearance="hint" style={{ marginVertical: 8 }}>
            {pendingPurchase
              ? `Enter your email to continue with ${planDisplayName(pendingPurchase)}`
              : ""}
          </Text>
          <Input
            testID="v3-email-gate-input"
            label="Email"
            value={email}
            autoCapitalize="none"
            keyboardType="email-address"
            onChangeText={(value: string) => setEmail(value)}
            onBlur={() => setEmailTouched(true)}
            status={emailError ? "danger" : "basic"}
            caption={emailError ?? undefined}
          />
          <Text category="c1" appearance="hint" style={{ marginTop: 8 }}>
            Entering an email doesn’t sign you in or create an account.
          </Text>
        </View>
      </Dialog>
    </View>
  );
};

export default ShopV3;
